import { useState, useRef, useEffect } from "react";
import { Share2, Facebook, MessageCircle, Mail, Link2, Check, X } from "lucide-react";

const PROD_ORIGIN = "https://www.vidalv.live";

function absoluteUrl(path) {
  // Alltid dela PROD-URL:en — även i preview-miljö — så delade länkar fungerar när sajten är live.
  // Om användare är på prod matchar det exakt; om inte, låter vi ändå prod-URL:en bli den delade.
  const cleanPath = path?.startsWith("/") ? path : `/${path || ""}`;
  return `${PROD_ORIGIN}${cleanPath}`;
}

function truncate(str, max = 200) {
  if (!str) return "";
  const clean = String(str).replace(/\s+/g, " ").trim();
  return clean.length <= max ? clean : clean.slice(0, max - 1).trimEnd() + "…";
}

/**
 * Dela-widget i Folkhem/Jugendstil-stil.
 * Props:
 *   title:   Rubrik som ska delas
 *   text:    Kort utdrag/beskrivning
 *   path:    Relativ sökväg (t.ex. "/anslag/abc-123") ELLER helt URL
 *   label:   Knapptext (default "Dela")
 *   variant: "button" (default) | "inline" (mindre)
 */
export default function ShareWidget({ title = "vidalv.live", text = "", path = "/", label = "Dela", variant = "button" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const url = /^https?:\/\//i.test(path) ? path : absoluteUrl(path);
  const shareText = truncate(text, 200);
  const composed = `${title}${shareText ? " – " + shareText : ""}`;

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url });
        setOpen(false);
        return true;
      } catch {
        /* user cancelled – ignore */
      }
    }
    return false;
  };

  const handleOpen = async () => {
    // On mobile prefer native sheet; if it fails/declines, open the dropdown.
    const ok = await handleNativeShare();
    if (!ok) setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: show the URL in a prompt so user can copy manually
      window.prompt("Kopiera länken:", url);
    }
  };

  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const messengerUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&app_id=140586622674265&redirect_uri=${encodeURIComponent(url)}`;
  const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(composed + " " + url)}`;
  const mailSubj = encodeURIComponent(title);
  const mailBody = encodeURIComponent(`${shareText}\n\n${url}\n\nHälsningar från vidalv.live`);
  const mailUrl = `mailto:?subject=${mailSubj}&body=${mailBody}`;

  const btnBase =
    variant === "inline"
      ? "inline-flex items-center gap-1.5 text-xs font-body font-semibold text-pine hover:text-pine-hover"
      : "inline-flex items-center gap-2 bg-bone-alt hover:bg-falu hover:text-parchment text-ink border border-warm-border px-4 py-2.5 rounded-sm text-sm font-body font-semibold transition-colors";

  return (
    <div ref={ref} className="relative inline-block" data-testid="share-widget">
      <button
        type="button"
        onClick={handleOpen}
        data-testid="share-widget-toggle"
        aria-haspopup="true"
        aria-expanded={open}
        className={btnBase}
      >
        <Share2 size={variant === "inline" ? 13 : 16} /> {label}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="share-widget-menu"
          className="absolute right-0 top-full mt-2 w-60 bg-parchment border border-warm-border rounded-sm shadow-lg z-40 overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-warm-border bg-bone-alt/60">
            <span className="text-[11px] font-body font-semibold uppercase tracking-wider text-bark">Dela via</span>
            <button onClick={() => setOpen(false)} aria-label="Stäng" className="text-bark/60 hover:text-ink p-0.5">
              <X size={14} />
            </button>
          </div>
          <a href={fbUrl} target="_blank" rel="noopener noreferrer" data-testid="share-facebook"
             onClick={() => setOpen(false)}
             className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-ink hover:bg-bone-alt no-underline transition-colors">
            <Facebook size={16} className="text-[#1877F2]" /> Facebook
          </a>
          <a href={messengerUrl} target="_blank" rel="noopener noreferrer" data-testid="share-messenger"
             onClick={() => setOpen(false)}
             className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-ink hover:bg-bone-alt no-underline transition-colors">
            <MessageCircle size={16} className="text-[#0084FF]" /> Messenger
          </a>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp"
             onClick={() => setOpen(false)}
             className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-ink hover:bg-bone-alt no-underline transition-colors">
            <MessageCircle size={16} className="text-[#25D366]" /> WhatsApp
          </a>
          <a href={mailUrl} data-testid="share-email"
             onClick={() => setOpen(false)}
             className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-ink hover:bg-bone-alt no-underline transition-colors">
            <Mail size={16} className="text-pine" /> E-post
          </a>
          <button onClick={copyLink} data-testid="share-copy"
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-ink hover:bg-bone-alt transition-colors text-left border-t border-warm-border">
            {copied ? <Check size={16} className="text-pine" /> : <Link2 size={16} className="text-falu" />}
            {copied ? "Länk kopierad!" : "Kopiera länk"}
          </button>
        </div>
      )}
    </div>
  );
}
